// Post-signup plumbing: add user to Resend audience + send placeholder welcome.
// Week 3 will replace placeholder content with real templates.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const inputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional().default(""),
  lastName: z.string().optional().default(""),
});

const RESEND_BASE = "https://api.resend.com";

async function resendFetch(path: string, init: RequestInit, apiKey: string) {
  const res = await fetch(`${RESEND_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

let cachedAudienceId: string | null = null;

async function getAudienceId(apiKey: string): Promise<string | null> {
  if (cachedAudienceId) return cachedAudienceId;
  if (process.env.RESEND_AUDIENCE_ID) {
    cachedAudienceId = process.env.RESEND_AUDIENCE_ID;
    return cachedAudienceId;
  }
  const { ok, body } = await resendFetch("/audiences", { method: "GET" }, apiKey);
  if (!ok) return null;
  const list = (body as any)?.data;
  const first = Array.isArray(list) && list.length > 0 ? list[0] : null;
  cachedAudienceId = first?.id || null;
  return cachedAudienceId;
}

export const postSignupHandler = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    try {
      return inputSchema.parse(data);
    } catch (e: any) {
      console.error("[post-signup] input validation failed", { received: data, error: e?.message });
      throw e;
    }
  })
  .handler(async ({ data, context }) => {
    // userId is taken from the authenticated session, never from the client
    // input — prevents callers from triggering welcome mail to other users.
    const userId = context.userId;
    console.log("[post-signup] handler invoked", { email: data.email, userId });
    const apiKey = process.env.RESEND_API_KEY;
    const apiKeyPresent = !!apiKey && apiKey.length > 0;
    console.log("[post-signup] RESEND_API_KEY present:", apiKeyPresent, "len:", apiKey?.length || 0);
    if (!apiKey) {
      console.error("[post-signup] RESEND_API_KEY not configured");
      return { ok: false };
    }


    // Look up the user's life stage + welcome-email idempotency stamp.
    // The welcome email is only sent ONCE per user, and ONLY after onboarding
    // has set life_stage — so the score label ("Vitality Score" for
    // Postmenopause, "Menopause Score" otherwise) is accurate.
    let stage: string | null = null;
    let alreadySent = false;
    let supabaseAdmin: any = null;
    try {
      ({ supabaseAdmin } = await import("@/integrations/supabase/client.server"));
      const { data: profile } = await supabaseAdmin
        .from("profiles")
        .select("life_stage, welcome_email_sent_at")
        .eq("id", userId)
        .maybeSingle();
      stage = (profile?.life_stage as string | null) ?? null;
      alreadySent = !!profile?.welcome_email_sent_at;
      // SECURITY: always send to the VERIFIED Supabase Auth email — never trust
      // the client-supplied address or the mirrored profiles.email column.
      const { getVerifiedAuthEmail } = await import(
        "@/integrations/supabase/auth-email.server"
      );
      const authEmail = await getVerifiedAuthEmail(userId);
      if (authEmail) data.email = authEmail;
    } catch (e: any) {
      console.warn("[post-signup] profile lookup failed", e?.message || String(e));
    }

    const shouldSendEmail = !!stage && !alreadySent;
    if (!shouldSendEmail) {
      console.log("[post-signup] skipping welcome email", {
        hasStage: !!stage, alreadySent,
      });
    }

    // Diagnostic: list audiences raw
    try {
      const audList = await resendFetch("/audiences", { method: "GET" }, apiKey);
      console.log("[post-signup] /audiences response", audList.status, JSON.stringify(audList.body).slice(0, 500));
    } catch (e: any) {
      console.error("[post-signup] /audiences fetch threw", e?.message || String(e));
    }

    // Internal diagnostic state — logged server-side only, never returned to client.
    const diag: {
      audienceId: string | null;
      contact?: Record<string, any>;
      email?: Record<string, any>;
    } = { audienceId: null };

    // Step 1 + 2: add to audience
    try {
      const audienceId = await getAudienceId(apiKey);
      diag.audienceId = audienceId;
      if (audienceId) {
        const contactRes = await resendFetch(
          `/audiences/${audienceId}/contacts`,
          {
            method: "POST",
            body: JSON.stringify({
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              unsubscribed: false,
            }),
          },
          apiKey,
        );
        diag.contact = { status: contactRes.status, body: contactRes.body };
      } else {
        diag.contact = { error: "no audience found" };
      }
    } catch (e: any) {
      diag.contact = { error: e?.message || String(e) };
    }

    // Step 3: send welcome email (only after onboarding, only once)
    if (shouldSendEmail) {
      const { subject, html, text } = buildWelcomeEmail(stage, data.firstName);
      const { logEmailSendAttempt } = await import("@/server/email/log.server");
      try {
        const emailRes = await resendFetch(
          "/emails",
          {
            method: "POST",
            body: JSON.stringify({
              from: "KnowMeno <support@knowmeno.com>",
              to: data.email,
              reply_to: "support@knowmeno.com",
              subject,
              html,
              text,
              tags: [{ name: "topic", value: "account_and_billing" }],
            }),
          },
          apiKey,
        );
        diag.email = { status: emailRes.status, body: emailRes.body };
        await logEmailSendAttempt({
          email_type: "signup_welcome",
          recipient_email: data.email,
          recipient_user_id: userId,
          subject,
          ok: emailRes.ok,
          provider_message_id:
            (emailRes.body as { id?: string })?.id ?? null,
          error_message: emailRes.ok
            ? null
            : `HTTP ${emailRes.status}: ${JSON.stringify(emailRes.body)}`,
          metadata: { stage: stage ?? null },
        });
        // Stamp idempotency marker on success (2xx).
        if (emailRes.ok && supabaseAdmin) {
          try {
            await supabaseAdmin
              .from("profiles")
              .update({ welcome_email_sent_at: new Date().toISOString() })
              .eq("id", userId)
              .is("welcome_email_sent_at", null);
          } catch (e: any) {
            console.warn("[post-signup] failed to stamp welcome_email_sent_at", e?.message || String(e));
          }
        }
      } catch (e: any) {
        diag.email = { error: e?.message || String(e) };
        await logEmailSendAttempt({
          email_type: "signup_welcome",
          recipient_email: data.email,
          recipient_user_id: userId,
          subject,
          ok: false,
          error_message: e?.message || String(e),
          metadata: { stage: stage ?? null },
        });
      }
    } else {
      diag.email = { skipped: true, reason: !stage ? "no_stage_yet" : "already_sent" };
    }

    // Server-side diagnostics only — do NOT leak internal IDs/response bodies to the client.
    console.log("[post-signup] diagnostics", JSON.stringify(diag).slice(0, 1000));

    return { ok: true };
  });


function buildWelcomeEmail(stage: string | null, firstName?: string) {
  if (stage === "Postmenopause") {
    return buildPostMenoWelcomeEmail(firstName);
  }
  const subject = "Welcome to KnowMeno — stop guessing, start knowing";
  const preheader = "Why this app exists, and how to make it work for you.";
  const ctaUrl = "https://knowmeno.com/app";
  const scoreLabel = "Menopause Impact Score";

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<meta name="color-scheme" content="light only" />
<meta name="supported-color-schemes" content="light" />
<title>${subject}</title>
</head>
<body style="margin:0;padding:0;background-color:#FBF8F5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1A0F1F;-webkit-text-size-adjust:100%;">
<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;color:#FBF8F5;">${preheader}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#FBF8F5;padding:24px 12px;">
  <tr><td align="center">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;">
      <tr><td align="center" style="padding:8px 0 20px 0;">
        <div style="font-family:Georgia,'Times New Roman',serif;font-size:28px;font-weight:700;letter-spacing:-0.5px;color:#a91f72;">KnowMeno</div>
      </td></tr>
      <tr><td style="background-color:#FFFFFF;border-radius:12px;padding:36px 32px;color:#1A0F1F;font-size:16px;line-height:1.6;">
        <p style="margin:0 0 16px 0;">Hi there,</p>
        <p style="margin:0 0 16px 0;">I'm Jannette, founder of KnowMeno. Before I tell you how to use it, let me tell you why I built it.</p>
        <p style="margin:0 0 16px 0;">In my late 30s, my body stopped feeling like mine. Brain fog. Hot flashes. Periods went haywire. My doctor said I was too young.</p>
        <p style="margin:0 0 16px 0;">Then came the wrong prescriptions. The wrong dosages. Night sweats, hair loss, and 62 pounds I didn't ask for. Six years of being dismissed.</p>
        <p style="margin:0 0 16px 0;">What finally changed everything? I tracked every symptom, every dose, every change — and walked into my next appointment with data. <strong>If I'd had real logged symptoms from day one, I would have gotten the right treatment six years sooner.</strong></p>
        <p style="margin:0 0 24px 0;">That's what KnowMeno is for. That's what your daily check-ins build.</p>
        <p style="margin:0 0 16px 0;"><strong>Here's how it works:</strong></p>
        <p style="margin:0 0 8px 0;"><strong>1. Tap "+ Hot flash" the moment one hits.</strong></p>
        <p style="margin:0 0 20px 0;">One tap. It timestamps automatically. No typing, no scrolling — just hit the button when you feel one coming on. Over time, the pattern shows you exactly when your body's flaring, and your Symptom Intelligence Report shows it to your provider in plain numbers.</p>
        <p style="margin:0 0 8px 0;"><strong>2. Open the app once a day for your check-in.</strong></p>
        <p style="margin:0 0 12px 0;">Takes about 30 seconds. Most people do it after dinner or before bed, when the day's symptoms are still fresh. You'll tap through:</p>
        <ul style="margin:0 0 20px 0;padding-left:22px;">
          <li style="margin:0 0 6px 0;">How you slept and your energy today</li>
          <li style="margin:0 0 6px 0;">Night sweats, mood shifts, brain fog (just the severity — your hot-flash count comes from your taps)</li>
          <li style="margin:0 0 6px 0;">HRT adherence (if you're on it)</li>
          <li style="margin:0 0 6px 0;">Period flow (if applicable)</li>
        </ul>
        <p style="margin:0 0 8px 0;"><strong>3. Watch your ${scoreLabel}.</strong></p>
        <p style="margin:0 0 20px 0;">Every day you log, you'll see a single number — your ${scoreLabel} — a 0–100 read of today's symptom load. Higher means a lighter day. The score weighs how intense each symptom is (a severe symptom counts for more than a mild one) and measures your day against a realistically tough one — so the number moves the way you actually feel. You'll also see it in plain words: Light, Mild, Moderate, High, or Very High. Watch it over the weeks and you'll see what your routine, HRT changes, or trigger avoidance are really doing.</p>
        <p style="margin:0 0 28px 0;">Every entry — every tap, every check-in — adds to your 90-day Symptom Intelligence Report. By day 90, you'll walk into your next appointment with data your provider can actually act on.</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto 28px auto;">
          <tr><td align="center" style="border-radius:8px;background-color:#a91f72;">
            <a href="${ctaUrl}" style="display:inline-block;background-color:#a91f72;color:#FFFFFF;text-decoration:none;font-weight:600;font-size:16px;padding:14px 28px;border-radius:8px;">Log your first check-in →</a>
          </td></tr>
        </table>
        <p style="margin:0 0 16px 0;">KnowMeno is free to use — your daily tracking, charts, and wearable connection never expire. When you're ready for the why — pattern and trigger detection, weekly insights, and the full Symptom Intelligence Report interpretation — KnowMeno Plus is $9.99/month or $79/year.</p>
        <p style="margin:0 0 16px 0;">If you have any questions, just reply — I read every one.</p>
        <p style="margin:0 0 4px 0;">— Jannette</p>
        <p style="margin:0 0 20px 0;">Founder, KnowMeno</p>
        <p style="margin:0;color:#1A0F1F;font-size:14px;">P.S. Need help? Email <a href="mailto:support@knowmeno.com" style="color:#4A1942;text-decoration:underline;">support@knowmeno.com</a> or visit our FAQ at <a href="https://knowmeno.com/faq" style="color:#4A1942;text-decoration:underline;">knowmeno.com/faq</a>.</p>
      </td></tr>
      <tr><td style="padding:24px 16px 8px 16px;color:#7A7383;font-size:12px;line-height:1.6;text-align:center;">
        <div style="margin-bottom:8px;">KnowMeno · Know your body. Empower your life.</div><div style="margin-bottom:8px;">Meno-IQ, LLC dba KnowMeno<br/>2541 N Dale Mabry Hwy PMB 235<br/>Tampa, FL 33607</div>
        <div style="margin-bottom:8px;">You're receiving this email because you signed up for a KnowMeno account.</div>
      </td></tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;

  const text = `Hi there,

I'm Jannette, founder of KnowMeno. Before I tell you how to use it, let me tell you why I built it.

In my late 30s, my body stopped feeling like mine. Brain fog. Hot flashes. Periods went haywire. My doctor said I was too young.

Then came the wrong prescriptions. The wrong dosages. Night sweats, hair loss, and 62 pounds I didn't ask for. Six years of being dismissed.

What finally changed everything? I tracked every symptom, every dose, every change — and walked into my next appointment with data. If I'd had real logged symptoms from day one, I would have gotten the right treatment six years sooner.

That's what KnowMeno is for. That's what your daily check-ins build.

Here's how it works:

1. Tap "+ Hot flash" the moment one hits.

One tap. It timestamps automatically. No typing, no scrolling — just hit the button when you feel one coming on. Over time, the pattern shows you exactly when your body's flaring, and your Symptom Intelligence Report shows it to your provider in plain numbers.

2. Open the app once a day for your check-in.

Takes about 30 seconds. Most people do it after dinner or before bed, when the day's symptoms are still fresh. You'll tap through:

- How you slept and your energy today
- Night sweats, mood shifts, brain fog (just the severity — your hot-flash count comes from your taps)
- HRT adherence (if you're on it)
- Period flow (if applicable)

3. Watch your ${scoreLabel}.

Every day you log, you'll see a single number — your ${scoreLabel} — a 0–100 read of today's symptom load. Higher means a lighter day. The score weighs how intense each symptom is (a severe symptom counts for more than a mild one) and measures your day against a realistically tough one — so the number moves the way you actually feel. You'll also see it in plain words: Light, Mild, Moderate, High, or Very High. Watch it over the weeks and you'll see what your routine, HRT changes, or trigger avoidance are really doing.

Every entry — every tap, every check-in — adds to your 90-day Symptom Intelligence Report. By day 90, you'll walk into your next appointment with data your provider can actually act on.

Log your first check-in: ${ctaUrl}

KnowMeno is free to use — your daily tracking, charts, and wearable connection never expire. When you're ready for the why — pattern and trigger detection, weekly insights, and the full Symptom Intelligence Report interpretation — KnowMeno Plus is $9.99/month or $79/year.

If you have any questions, just reply — I read every one.

— Jannette
Founder, KnowMeno

P.S. Need help? Email support@knowmeno.com or visit our FAQ at knowmeno.com/faq.

---
KnowMeno · Know your body. Empower your life.
Meno-IQ, LLC dba KnowMeno
2541 N Dale Mabry Hwy PMB 235
Tampa, FL 33607

You're receiving this email because you signed up for a KnowMeno account.
`;

  return { subject, html, text };
}


function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function buildPostMenoWelcomeEmail(firstName?: string) {
  const subject = "Welcome — for the next 30 years";
  const preheader = "My mom and sisters had no playbook. I built one for you.";
  const ctaUrl = "https://knowmeno.com/app";
  const name = (firstName || "").trim();
  const greeting = name ? `Hi ${escapeHtml(name)},` : "Hi there,";
  const greetingText = name ? `Hi ${name},` : "Hi there,";

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<meta name="color-scheme" content="light only" />
<meta name="supported-color-schemes" content="light" />
<title>${subject}</title>
</head>
<body style="margin:0;padding:0;background-color:#FBF8F5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1A0F1F;-webkit-text-size-adjust:100%;">
<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;color:#FBF8F5;">${preheader}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#FBF8F5;padding:24px 12px;">
  <tr><td align="center">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;">
      <tr><td align="center" style="padding:8px 0 20px 0;">
        <div style="font-family:Georgia,'Times New Roman',serif;font-size:28px;font-weight:700;letter-spacing:-0.5px;color:#a91f72;">KnowMeno</div>
      </td></tr>
      <tr><td style="background-color:#FFFFFF;border-radius:12px;padding:36px 32px;color:#1A0F1F;font-size:16px;line-height:1.6;">
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 16px 0;">I'm Jannette, founder of KnowMeno. Before I tell you how to use it, let me tell you why I built it — and why I built this part of it specifically for you.</p>
        <p style="margin:0 0 16px 0;">I'm not in your stage yet. I'm in peri. But I've been watching the women ahead of me — my mom, my older sisters — navigate post-menopause without a playbook. They went to appointments and got told everything was "normal aging." They watched their bone density slip. They noticed creeping LDL but their doctors waited. They missed screenings because nobody reminded them, and by the time something showed up, it was further along than it should have been.</p>
        <p style="margin:0 0 16px 0;">That's not their fault. The system doesn't build a longevity plan for women in this stage. Every other app stops at menopause like it's the end of the story. But you'll spend <strong>30+ years</strong> in post-menopause. A third of your life. The decisions you make now — about screening, labs, sleep, HRT, exercise — shape every one of those years.</p>
        <p style="margin:0 0 24px 0;">So I built the playbook.</p>
        <p style="margin:0 0 16px 0;"><strong>Here's how to use it:</strong></p>
        <p style="margin:0 0 8px 0;"><strong>1. Log your screenings.</strong></p>
        <p style="margin:0 0 20px 0;">Tap your Care tab and add the dates of your last mammogram, DEXA, lipid panel, A1C, colonoscopy, and any others you remember. KnowMeno tracks when each one is due. You won't have to remember — we'll tell you.</p>
        <p style="margin:0 0 8px 0;"><strong>2. Tap your check-in once a day.</strong></p>
        <p style="margin:0 0 20px 0;">Takes 30 seconds. Sleep, energy, any symptoms still kicking around, HRT response. Most people do it after dinner or before bed. Over time, the picture sharpens.</p>
        <p style="margin:0 0 8px 0;"><strong>3. Watch your Vitality Score.</strong></p>
        <p style="margin:0 0 20px 0;">Every day, a single number from 0 to 100 based on how today is tracking against your last 14 days. Up, down, or steady. It tells you whether what you're doing is actually working — at a glance.</p>
        <p style="margin:0 0 8px 0;"><strong>4. Walk into every annual visit with your report.</strong></p>
        <p style="margin:0 0 24px 0;">Once a year, KnowMeno generates your Annual Wellness Summary — a clinical-grade PDF covering symptoms, screening status, lab trends, bone and cardiovascular markers, and risk factors. You hand it to your doctor. The conversation shifts from "how have you been?" to "let's talk about what your trends are showing."</p>
        <p style="margin:0 0 8px 0;"><strong>Other apps help you survive menopause.</strong></p>
        <p style="margin:0 0 28px 0;"><strong>KnowMeno helps you thrive in the 30+ years after.</strong></p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto 28px auto;">
          <tr><td align="center" style="border-radius:8px;background-color:#a91f72;">
            <a href="${ctaUrl}" style="display:inline-block;background-color:#a91f72;color:#FFFFFF;text-decoration:none;font-weight:600;font-size:16px;padding:14px 28px;border-radius:8px;">Log your first check-in →</a>
          </td></tr>
        </table>
        <p style="margin:0 0 16px 0;">KnowMeno is free to use — your daily tracking, scores, charts, and wearable connection never expire. When you're ready to go deeper — pattern detection, weekly insights, and full report interpretation — KnowMeno Plus is $9.99/month or $79/year.</p>
        <p style="margin:0 0 16px 0;">If you have any questions, just reply — I read every one.</p>
        <p style="margin:0 0 4px 0;">— Jannette</p>
        <p style="margin:0 0 20px 0;">Founder, KnowMeno</p>
        <p style="margin:0;color:#1A0F1F;font-size:14px;">P.S. Need help? Email <a href="mailto:support@knowmeno.com" style="color:#4A1942;text-decoration:underline;">support@knowmeno.com</a> or visit our FAQ at <a href="https://knowmeno.com/faq" style="color:#4A1942;text-decoration:underline;">knowmeno.com/faq</a>.</p>
      </td></tr>
      <tr><td style="padding:24px 16px 8px 16px;color:#7A7383;font-size:12px;line-height:1.6;text-align:center;">
        <div style="margin-bottom:8px;">KnowMeno · Know your body. Empower your life.</div><div style="margin-bottom:8px;">Meno-IQ, LLC dba KnowMeno<br/>2541 N Dale Mabry Hwy PMB 235<br/>Tampa, FL 33607</div>
        <div style="margin-bottom:8px;">You're receiving this email because you signed up for a KnowMeno account.</div>
      </td></tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;

  const text = `${greetingText}

I'm Jannette, founder of KnowMeno. Before I tell you how to use it, let me tell you why I built it — and why I built this part of it specifically for you.

I'm not in your stage yet. I'm in peri. But I've been watching the women ahead of me — my mom, my older sisters — navigate post-menopause without a playbook. They went to appointments and got told everything was "normal aging." They watched their bone density slip. They noticed creeping LDL but their doctors waited. They missed screenings because nobody reminded them, and by the time something showed up, it was further along than it should have been.

That's not their fault. The system doesn't build a longevity plan for women in this stage. Every other app stops at menopause like it's the end of the story. But you'll spend 30+ years in post-menopause. A third of your life. The decisions you make now — about screening, labs, sleep, HRT, exercise — shape every one of those years.

So I built the playbook.

Here's how to use it:

1. Log your screenings.

Tap your Care tab and add the dates of your last mammogram, DEXA, lipid panel, A1C, colonoscopy, and any others you remember. KnowMeno tracks when each one is due. You won't have to remember — we'll tell you.

2. Tap your check-in once a day.

Takes 30 seconds. Sleep, energy, any symptoms still kicking around, HRT response. Most people do it after dinner or before bed. Over time, the picture sharpens.

3. Watch your Vitality Score.

Every day, a single number from 0 to 100 based on how today is tracking against your last 14 days. Up, down, or steady. It tells you whether what you're doing is actually working — at a glance.

4. Walk into every annual visit with your report.

Once a year, KnowMeno generates your Annual Wellness Summary — a clinical-grade PDF covering symptoms, screening status, lab trends, bone and cardiovascular markers, and risk factors. You hand it to your doctor. The conversation shifts from "how have you been?" to "let's talk about what your trends are showing."

Other apps help you survive menopause.
KnowMeno helps you thrive in the 30+ years after.

Log your first check-in: ${ctaUrl}

KnowMeno is free to use — your daily tracking, scores, charts, and wearable connection never expire. When you're ready to go deeper — pattern detection, weekly insights, and full report interpretation — KnowMeno Plus is $9.99/month or $79/year.

If you have any questions, just reply — I read every one.

— Jannette
Founder, KnowMeno

P.S. Need help? Email support@knowmeno.com or visit our FAQ at knowmeno.com/faq.

---
KnowMeno · Know your body. Empower your life.
Meno-IQ, LLC dba KnowMeno
2541 N Dale Mabry Hwy PMB 235
Tampa, FL 33607

You're receiving this email because you signed up for a KnowMeno account.
`;

  return { subject, html, text };
}
